import { useFilters } from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { competitionOptionsAtom, selectedCompetitionsAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonOptionsAtom, selectedSeasonsAtom } from '@/atoms/filters/highLevel/seasons';
import { teamOptionsAtom, selectedTeamsAtom } from '@/atoms/filters/highLevel/teams';
import {
  comparisonSelectedCompetitionsAtom,
  comparisonSelectedSeasonsAtom,
  comparisonSelectedTeamsAtom,
  playerComparisonCompetitionOptionsAtom,
  playerComparisonSeasonOptionsAtom,
  playerComparisonTeamsAtom,
  playerComparisonTeamsOptionsAtom,
} from '@/atoms/filters/player/playerComparisonFilters';
import {
  comparisonFilteredPlayerGamesAtom,
  playerComparisonSelectedGamesAtom,
} from '@/atoms/filters/player/playerComparisonGames';
import { filteredPlayerGamesAtom, playerSelectedGamesAtom } from '@/atoms/filters/player/playerGames';
import { playerComparisonCyclesOrderedAtom } from '@/atoms/queries/player/playerComparisonCycles';
import { playerCyclesOrderedAtom, playerTeamsAtom } from '@/atoms/queries/players';
import { useGetFilteredOptions } from '@/hooks/useGetFilteredOptions';
import { GamesFilterType } from '@/hooks/useGetGamesByFilter';
import { getDistinctOrderedCompetitionOptions, getOrderedTeamOptions } from '@/utils/cycles';

// When a competition or season filter is cleared (ids = []), the bio must show all available options for the entity —
// not just those that survive the remaining active game filters. These unwrapped atoms read directly from cycle-derived
// option lists, bypassing the game filter pipeline, to get the complete list
const allPrimaryCompetitionOptionsAtom = unwrap(competitionOptionsAtom, prev => prev || []);
const allPrimarySeasonOptionsAtom = unwrap(seasonOptionsAtom, prev => prev || []);
const allComparisonCompetitionOptionsAtom = unwrap(playerComparisonCompetitionOptionsAtom, prev => prev || []);
const allComparisonSeasonOptionsAtom = unwrap(playerComparisonSeasonOptionsAtom, prev => prev || []);

/**
 * Resolves which options to display for a filter bio row.
 * - null ids = untouched (show defaults from the active cycle)
 * - non-empty ids = user has made a selection (show that selection)
 * - empty ids [] = user cleared the filter (show everything available)
 */
const resolveFilterDisplayOptions = <T>(
  selectedIds: number[] | null,
  selectedOptions: T[],
  defaultOptions: T[],
  allOptions: T[],
): T[] => {
  if (selectedIds === null) return defaultOptions;
  return selectedIds.length ? selectedOptions : allOptions;
};

export const usePlayerFilterDisplayOptions = () => {
  const playerCyclesOrdered = useAtomValue(playerCyclesOrderedAtom);
  const playerTeams = useAtomValue(playerTeamsAtom);
  const [{ competitionIds: selectedCompetitionIds, seasonIds: selectedSeasonIds }] = useFilters('main');
  const selectedCompetitions = useAtomValue(selectedCompetitionsAtom);
  const selectedSeasons = useAtomValue(selectedSeasonsAtom);
  const selectedTeams = useAtomValue(selectedTeamsAtom);
  const allCompetitionOptions = useAtomValue(allPrimaryCompetitionOptionsAtom);
  const allSeasonOptions = useAtomValue(allPrimarySeasonOptionsAtom);

  const filteredCompetitionOptions = useGetFilteredOptions(
    competitionOptionsAtom,
    filteredPlayerGamesAtom,
    playerSelectedGamesAtom,
    GamesFilterType.COMPETITION,
  );
  const sortedCompetitionOptions = getDistinctOrderedCompetitionOptions(
    playerCyclesOrdered,
    filteredCompetitionOptions,
  );
  const allSortedCompetitionOptions = getDistinctOrderedCompetitionOptions(playerCyclesOrdered, allCompetitionOptions);
  const competitionsToDisplay = resolveFilterDisplayOptions(
    selectedCompetitionIds,
    selectedCompetitions,
    sortedCompetitionOptions,
    allSortedCompetitionOptions,
  );

  const filteredSeasonOptions = useGetFilteredOptions(
    seasonOptionsAtom,
    filteredPlayerGamesAtom,
    playerSelectedGamesAtom,
    GamesFilterType.SEASON,
  );
  const seasonsToDisplay = resolveFilterDisplayOptions(
    selectedSeasonIds,
    selectedSeasons,
    filteredSeasonOptions,
    allSeasonOptions,
  );

  const filteredTeamOptions = useGetFilteredOptions(
    teamOptionsAtom,
    filteredPlayerGamesAtom,
    playerSelectedGamesAtom,
    GamesFilterType.TEAM,
  );
  const sortedTeamOptions = getOrderedTeamOptions(playerCyclesOrdered, filteredTeamOptions, playerTeams);
  const teamsToDisplay = selectedTeams.length ? selectedTeams : sortedTeamOptions;

  return { competitionsToDisplay, seasonsToDisplay, teamsToDisplay };
};

export const useComparisonPlayerFilterDisplayOptions = () => {
  const playerCyclesOrdered = useAtomValue(playerComparisonCyclesOrderedAtom);
  const comparisonPlayerTeams = useAtomValue(playerComparisonTeamsAtom);
  const selectedCompetitions = useAtomValue(comparisonSelectedCompetitionsAtom);
  const selectedSeasons = useAtomValue(comparisonSelectedSeasonsAtom);
  const selectedTeams = useAtomValue(comparisonSelectedTeamsAtom);
  const [comparisonFilters] = useFilters('comparison');
  const selectedCompetitionIds = comparisonFilters.competitionIds;
  const selectedSeasonIds = comparisonFilters.seasonIds;
  const allCompetitionOptions = useAtomValue(allComparisonCompetitionOptionsAtom);
  const allSeasonOptions = useAtomValue(allComparisonSeasonOptionsAtom);

  const filteredCompetitionOptions = useGetFilteredOptions(
    playerComparisonCompetitionOptionsAtom,
    comparisonFilteredPlayerGamesAtom,
    playerComparisonSelectedGamesAtom,
    GamesFilterType.COMPETITION,
  );
  const sortedCompetitionOptions = getDistinctOrderedCompetitionOptions(
    playerCyclesOrdered,
    filteredCompetitionOptions,
  );
  const allSortedCompetitionOptions = getDistinctOrderedCompetitionOptions(playerCyclesOrdered, allCompetitionOptions);
  const competitionsToDisplay = resolveFilterDisplayOptions(
    selectedCompetitionIds,
    selectedCompetitions,
    sortedCompetitionOptions,
    allSortedCompetitionOptions,
  );

  const filteredSeasonOptions = useGetFilteredOptions(
    playerComparisonSeasonOptionsAtom,
    comparisonFilteredPlayerGamesAtom,
    playerComparisonSelectedGamesAtom,
    GamesFilterType.SEASON,
  );
  const seasonsToDisplay = resolveFilterDisplayOptions(
    selectedSeasonIds,
    selectedSeasons,
    filteredSeasonOptions,
    allSeasonOptions,
  );

  const filteredTeamOptions = useGetFilteredOptions(
    playerComparisonTeamsOptionsAtom,
    comparisonFilteredPlayerGamesAtom,
    playerComparisonSelectedGamesAtom,
    GamesFilterType.TEAM,
  );
  const sortedTeamOptions = getOrderedTeamOptions(playerCyclesOrdered, filteredTeamOptions, comparisonPlayerTeams);
  const teamsToDisplay = selectedTeams.length ? selectedTeams : sortedTeamOptions;

  return { competitionsToDisplay, seasonsToDisplay, teamsToDisplay };
};
